import React from 'react';
import { ExternalLink, Headphones } from 'lucide-react';

// Popular CRM platforms used by RV dealerships
const CRM_PLATFORMS = [
  { name: 'VinSolutions', url: 'https://www.vinsolutions.com', description: 'Automotive CRM & lead management', logo: 'https://logo.clearbit.com/vinsolutions.com', color: '#0066CC' },
  { name: 'DealerSocket', url: 'https://www.dealersocket.com', description: 'Dealer management & CRM software', logo: 'https://logo.clearbit.com/dealersocket.com', color: '#00A651' },
  { name: 'Salesforce', url: 'https://www.salesforce.com', description: 'World\'s #1 CRM platform', logo: 'https://logo.clearbit.com/salesforce.com', color: '#00A1E0' },
  { name: 'HubSpot', url: 'https://www.hubspot.com', description: 'Free CRM & marketing automation', logo: 'https://logo.clearbit.com/hubspot.com', color: '#FF7A59' },
  { name: 'Zoho CRM', url: 'https://www.zoho.com/crm', description: 'Affordable CRM for all business sizes', logo: 'https://logo.clearbit.com/zoho.com', color: '#E42527' },
  { name: 'DealerCenter', url: 'https://www.dealercenter.com', description: 'Dealer management system', logo: 'https://logo.clearbit.com/dealercenter.com', color: '#1A365D' },
  { name: 'AutoRaptor', url: 'https://www.autoraptor.com', description: 'Auto dealer CRM software', logo: 'https://logo.clearbit.com/autoraptor.com', color: '#FF4500' },
  { name: 'elead CRM', url: 'https://www.eleadcrm.com', description: 'Automotive CRM & digital retailing', logo: 'https://logo.clearbit.com/eleadcrm.com', color: '#003366' },
  { name: 'Pipedrive', url: 'https://www.pipedrive.com', description: 'Sales-focused CRM', logo: 'https://logo.clearbit.com/pipedrive.com', color: '#017737' },
  { name: 'Monday.com', url: 'https://www.monday.com', description: 'Work OS & CRM platform', logo: 'https://logo.clearbit.com/monday.com', color: '#FF3D57' },
  { name: 'Freshsales', url: 'https://www.freshworks.com/crm/sales', description: 'AI-powered sales CRM', logo: 'https://logo.clearbit.com/freshworks.com', color: '#F26522' },
  { name: 'Copper', url: 'https://www.copper.com', description: 'CRM for Google Workspace', logo: 'https://logo.clearbit.com/copper.com', color: '#F7941D' }
];

const CRMHubEmbed = ({ isDark }) => {
  return (
    <div data-testid="crm-hub-embed">
      {/* Header */}
      <div className="mb-6">
        <h2 className={`text-xl font-bold flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
          <Headphones size={24} />
          CRM Hub
        </h2>
        <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
          Quick access to popular CRM platforms for RV dealerships
        </p>
      </div>

      {/* CRM Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {CRM_PLATFORMS.map((crm) => (
          <a
            key={crm.name}
            href={crm.url}
            target="_blank"
            rel="noopener noreferrer"
            className={`group rounded-xl p-4 transition-all duration-200 hover:scale-[1.02] ${
              isDark 
                ? 'bg-slate-800 border border-slate-700 hover:border-slate-600' 
                : 'bg-white border border-slate-200 hover:border-slate-300 shadow-sm hover:shadow-md'
            }`}
            data-testid={`crm-${crm.name.toLowerCase().replace(/\s/g, '-')}`}
          >
            <div className="flex items-start gap-3">
              <div 
                className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                style={{ backgroundColor: `${crm.color}15` }}
              >
                <img 
                  src={crm.logo} 
                  alt={crm.name}
                  className="w-6 h-6 object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <div 
                  className="w-6 h-6 rounded items-center justify-center text-xs font-bold text-white hidden"
                  style={{ backgroundColor: crm.color }}
                >
                  {crm.name.charAt(0)}
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1">
                  <h3 className={`font-semibold text-sm truncate ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    {crm.name}
                  </h3>
                  <ExternalLink size={12} className={`shrink-0 opacity-0 group-hover:opacity-100 transition-opacity ${isDark ? 'text-slate-400' : 'text-slate-500'}`} />
                </div>
                <p className={`text-xs truncate ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                  {crm.description}
                </p>
              </div>
            </div>
          </a>
        ))}
      </div>

      {/* Info Footer */}
      <div className={`mt-6 p-4 rounded-xl ${isDark ? 'bg-slate-800/50' : 'bg-slate-50'}`}>
        <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
          <strong>Tip:</strong> Click any CRM to open it in a new tab. Use RV Match AI's lead data to enrich your CRM records.
        </p>
      </div>
    </div>
  );
};

export default CRMHubEmbed;
